import * as React from 'react'
import { cn } from '../../lib/cn'
import type { SpineChipData } from './types'

/* -------------------------------------------------------------------------- */
/* SpineChip — small system chip used inside SpineTrack                       */
/* -------------------------------------------------------------------------- */

export interface SpineChipProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Chip data — title + optional icon + optional sub + optional tag. */
  chip: SpineChipData
}

const SPINE_CHIP_GRADIENT =
  'linear-gradient(180deg, color-mix(in srgb, var(--pe-border-card) 18%, transparent) 0%, var(--pe-bg-sys) 100%)'

/**
 * Small system chip in the spine track. Width fixed at 180px per
 * mockup. Mono-typography title, optional icon, optional right-side
 * tag pill (defaults to `sys`).
 *
 * Source of truth: `mockups/unified-pipeline.html` `.sys-chip-small`.
 */
export const SpineChip = React.forwardRef<HTMLDivElement, SpineChipProps>(function SpineChip(
  { className, chip, style, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'shrink-0 rounded-[10px] px-3 py-2.5 border transition-colors duration-150',
        className,
      )}
      style={{
        width: 180,
        backgroundImage: SPINE_CHIP_GRADIENT,
        borderColor: 'var(--pe-border-sys)',
        ...style,
      }}
      data-testid="pipeline-spine-chip"
      data-chip-id={chip.id}
      {...rest}
    >
      <div className="flex items-center gap-2 mb-0.5">
        <div
          className="w-[22px] h-[22px] shrink-0 flex items-center justify-center rounded"
          style={{
            background: 'color-mix(in srgb, var(--pe-text-primary) 4%, transparent)',
            color: 'var(--pe-text-tertiary)',
          }}
          aria-hidden
        >
          {chip.icon}
        </div>
        <span
          className="flex-1 min-w-0 truncate font-mono text-[11px]"
          style={{ color: 'var(--pe-text-secondary)' }}
        >
          {chip.title}
        </span>
        <span
          className="shrink-0 rounded-[2px] border font-mono uppercase"
          style={{
            fontSize: 7.5,
            padding: '1px 4px',
            letterSpacing: '0.10em',
            color: 'var(--pe-text-quiet)',
            background: 'color-mix(in srgb, var(--pe-text-primary) 2.5%, transparent)',
            borderColor: 'var(--pe-border-sys)',
          }}
        >
          {chip.tag ?? 'sys'}
        </span>
      </div>
      {chip.sub && (
        <div
          className="pl-[30px] font-mono"
          style={{
            fontSize: 9.5,
            lineHeight: 1.35,
            color: 'var(--pe-text-muted)',
          }}
        >
          {chip.sub}
        </div>
      )}
    </div>
  )
})

/* -------------------------------------------------------------------------- */
/* SpineConn — arrow-tipped connector between two spine chips                 */
/* -------------------------------------------------------------------------- */

/** Internal — 1.5px line with an arrowhead, fixed 14px wide. */
function SpineConn() {
  return (
    <span
      aria-hidden
      className="relative shrink-0"
      style={{
        width: 14,
        height: 1.5,
        background: 'var(--pe-border-card)',
      }}
    >
      <span
        className="absolute"
        style={{
          right: -1,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 0,
          height: 0,
          borderLeft: '4px solid var(--pe-border-card)',
          borderTop: '3px solid transparent',
          borderBottom: '3px solid transparent',
        }}
      />
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* SpineTrack — horizontal chain of system chips                              */
/* -------------------------------------------------------------------------- */

export interface SpineTrackProps extends React.HTMLAttributes<HTMLDivElement> {
  chips: SpineChipData[]
  /** Solid leader (newuser) vs dotted leader (existing). Default: solid. */
  leaderStyle?: 'solid' | 'dotted'
}

/**
 * Horizontal chain of system chips that runs to the right of a lane:
 *
 *   [leader stub] [chip] -> [chip] -> [chip] [trailing line]
 *
 * The leader stub style differentiates the two parallel rows (dotted
 * for the existing-user row, solid for the new-user row). The
 * trailing flex line fills the remaining row width so the track
 * always extends across the branch column.
 *
 * Source of truth: `mockups/unified-pipeline.html` `.spine-track`.
 */
export const SpineTrack = React.forwardRef<HTMLDivElement, SpineTrackProps>(function SpineTrack(
  { className, chips, leaderStyle = 'solid', style, ...rest },
  ref,
) {
  const leaderBackground =
    leaderStyle === 'dotted'
      ? 'repeating-linear-gradient(90deg, transparent 0, transparent 3px, var(--pe-border-card) 3px, var(--pe-border-card) 7px)'
      : 'var(--pe-border-card)'

  return (
    <div
      ref={ref}
      className={cn('flex-1 min-w-0 ml-6 flex items-center gap-2.5 px-1 relative', className)}
      style={style}
      data-testid="pipeline-spine-track"
      data-leader={leaderStyle}
      {...rest}
    >
      <span
        aria-hidden
        className="shrink-0"
        style={{ width: 18, height: 1.5, background: leaderBackground }}
        data-testid="pipeline-spine-leader"
      />
      {chips.map((chip, idx) => (
        <React.Fragment key={chip.id}>
          <SpineChip chip={chip} />
          {idx < chips.length - 1 && <SpineConn />}
        </React.Fragment>
      ))}
      {/* Trailing line — fills remaining flex width to anchor the track. */}
      <span
        aria-hidden
        className="flex-1"
        style={{ height: 1.5, minWidth: 10, background: 'var(--pe-border-card)' }}
      />
    </div>
  )
})
